import React, { useState } from 'react';
import { Car, Home, Plane, Building2, Smartphone, Watch, Anchor, LucideIcon, ImageOff, ShoppingCart, Plus, Minus } from 'lucide-react';
import { LuxuryItem } from '../types';
import { formatCurrency, formatBitcoin, formatBitcoinDetailed } from '../utils/formatters';
import { useCart } from '../context/CartContext';

interface LuxuryCardProps {
  item: LuxuryItem;
  bitcoinPrice: number;
}

const iconMap: Record<string, LucideIcon> = {
  'car': Car,
  'home': Home,
  'plane': Plane,
  'building-2': Building2,
  'smartphone': Smartphone,
  'watch': Watch,
  'anchor': Anchor
};

export const LuxuryCard: React.FC<LuxuryCardProps> = ({ item, bitcoinPrice }) => {
  const [imageError, setImageError] = useState(false);
  const [showDetailedBtc, setShowDetailedBtc] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const IconComponent = (iconMap[item.icon] || Home) as LucideIcon;
  const bitcoinsNeeded = !bitcoinPrice || bitcoinPrice <= 0 ? 0 : item.price / bitcoinPrice;
  const { addItem, state, updateQuantity, removeItem } = useCart();
  const isMobile = 'ontouchstart' in window;

  const cartItem = state.items.find(i => i.name === item.name);

  const handleImageError = () => {
    console.error(`Failed to load image for ${item.name}`);
    setImageError(true);
  };

  const handleBtcClick = () => {
    if (isMobile) {
      setShowDetailedBtc(!showDetailedBtc);
    }
  };

  const handleAddToCart = () => {
    setIsAdding(true);
    addItem(item);
    setTimeout(() => setIsAdding(false), 500);
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity === 0) {
      removeItem(item.name);
    } else {
      updateQuantity(item.name, newQuantity);
    }
  };

  return (
    <div className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 will-change-transform relative z-10 hover:z-20">
      <div className="relative aspect-[16/9] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent z-10" />
        {imageError ? (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <ImageOff className="w-12 h-12 text-gray-400" />
          </div>
        ) : (
          <img 
            src={item.image} 
            alt={item.name}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500 will-change-transform"
            onError={handleImageError}
            loading="lazy"
          />
        )}
        <div className="absolute bottom-3 left-3 z-20">
          <div className="flex items-center gap-1.5 text-white">
            <IconComponent className="w-4 h-4 sm:w-6 sm:h-6" />
            <h3 className="text-sm sm:text-xl font-bold drop-shadow-lg leading-tight">{item.name}</h3>
          </div>
        </div>
      </div>
      <div className="p-3 sm:p-6">
        <p className="text-gray-600 mb-3 text-xs sm:text-base line-clamp-2 leading-snug">{item.description}</p>
        <div className="space-y-1.5 sm:space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 text-xs sm:text-base">Price:</span>
            <span className="font-bold text-xs sm:text-base">{formatCurrency(item.price)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600 text-xs sm:text-base">Bitcoin needed:</span>
            {!bitcoinPrice || bitcoinPrice <= 0 ? (
              <span className="text-gray-400 font-bold text-xs sm:text-base">Loading...</span>
            ) : (
              <button 
                className="text-orange-500 font-bold text-xs sm:text-base cursor-pointer focus:outline-none active:opacity-75"
                onClick={handleBtcClick}
                onMouseEnter={() => !isMobile && setShowDetailedBtc(true)}
                onMouseLeave={() => !isMobile && setShowDetailedBtc(false)}
              >
                ₿ {showDetailedBtc ? formatBitcoinDetailed(bitcoinsNeeded) : formatBitcoin(bitcoinsNeeded)}
              </button>
            )}
          </div>
          {!cartItem ? (
            <button
              onClick={handleAddToCart}
              disabled={isAdding}
              className={`w-full mt-4 px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-all duration-300 ${
                isAdding
                  ? 'bg-green-500 text-white'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'
              }`}
            >
              <ShoppingCart className="w-4 h-4" />
              {isAdding ? 'Added!' : 'Add to Cart'}
            </button>
          ) : (
            <div className="flex items-center justify-between mt-4 bg-orange-50 rounded-lg p-2">
              <button
                onClick={() => handleQuantityChange(Math.max(0, cartItem.quantity - 1))}
                className="p-1.5 hover:bg-orange-100 rounded-lg transition-colors"
              >
                <Minus className="w-4 h-4 text-orange-500" />
              </button>
              <span className="font-medium text-orange-600">{cartItem.quantity}</span>
              <button
                onClick={() => handleQuantityChange(cartItem.quantity + 1)}
                className="p-1.5 hover:bg-orange-100 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4 text-orange-500" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};